import React from 'react';
import classNames from 'classnames';
import { PageProps, Link, graphql, useStaticQuery } from 'gatsby';
import BenefitItem from '@/components/benefit/benefit-item';
import heroVideo from '@/images/backgrounds/hero-video.mp4';
import heroPoster from '@/images/backgrounds/hero-poster.png';
import ArrowIcon from '@/images/svgs/arrow.svg';
import WhiteArrowIcon from '@/images/svgs/arrow-white.svg';
import AirstageIcon from '@/images/svgs/AirstageCloud.svg';
import benefitItems from '@/content/benefits.json';
import { useStyles } from '@/hooks/useStyles';

const HeroSection: React.FC<PageProps> = () => {
  const { joinClasses } = useStyles();
  const {
    allFile: { edges: benefitFiles }
  } = useStaticQuery(graphql`
    query BenefitsQuery {
      allFile(
        filter: {
          extension: { eq: "svg" }
          absolutePath: { regex: "/benefits/" }
        }
      ) {
        edges {
          node {
            id
            publicURL
            absolutePath
            name
          }
        }
      }
    }
  `);

  benefitItems.map((benefit) => {
    const benefitNode = benefitFiles.find(
      (benefitFile) =>
        benefitFile.node.name.toLowerCase() === benefit.id.toLowerCase()
    );

    if (benefitNode) {
      benefit.icon = benefitNode.node.publicURL;
    }

    return benefit;
  });

  const heroClasses = classNames(
    'relative h-screen mt-[85px] lg:mt-0',
    'after:z-0 after:absolute after:bottom-0 after:left-0 after:w-full after:h-full',
    'after:bg-gradient-to-t after:from-black/50 after:to-transparent'
  );

  const desktopClasses = {
    benefitsContainer: classNames(
      'lg:absolute lg:left-[50%] lg:translate-x-[-50%] lg:translate-y-[-2.094rem] lg:z-[11] lg:my-0'
    ),
    benefitsGrid: classNames('lg:grid-cols-4 lg:gap-[3rem] lg:items-[unset]'),
    slogan: classNames('md:w-[unset]')
  };
  const mobileClasses = {
    benefitsContainer: classNames('wrapper w-full my-[4.38rem]'),
    benefitsGrid: classNames(
      'w-full grid grid-cols-1 sm:grid-cols-2 gap-4 items-stretch'
    ),
    slogan: classNames(
      'text-[2rem] font-["SegoeUIItalic"] italic leading-normal absolute bottom-[20%] left-[50%] translate-x-[-50%] w-11/12 z-10 text-white'
    )
  };

  return (
    <>
      <section className={heroClasses}>
        <video
          muted
          autoPlay
          loop
          playsInline
          poster={heroPoster}
          className="absolute w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          Your browser does not allow <code>video</code>.
        </video>

        <div className="wrapper w-full absolute top-[15%] sm:top-[50%] left-[50%] translate-x-[-50%] sm:translate-y-[-50%] z-10 text-white">
          <div className="flex flex-wrap items-end mb-8">
            <AirstageIcon className="max-w-[42rem] w-full min-w-[200px] mr-4" />
          </div>
          <p className="max-w-[42.0625rem] text-title-2 md:text-title md:!font-normal leading-[4rem]">
            Virtual Private Building Management Systems
          </p>
          <Link
            to="/overview"
            className="inline-flex group bg-white text-primary justity-between items-center py-[0.62rem] px-[1.25rem] mt-6 rounded-[0.625rem] hover:bg-primary hover:text-white transition-all"
          >
            <span className="text-button mr-4">Learn more</span>
            <ArrowIcon className="inline-block group-hover:hidden group-hover:opacity-0" />
            <WhiteArrowIcon className="hidden opacity-0 group-hover:inline-block group-hover:opacity-100" />
          </Link>
        </div>

        <p
          className={joinClasses([desktopClasses.slogan, mobileClasses.slogan])}
        >
          ...deliver the power of a traditional BMS at a fraction of the cost
        </p>
      </section>
      <div
        className={joinClasses([
          desktopClasses.benefitsContainer,
          mobileClasses.benefitsContainer
        ])}
      >
        <div
          className={joinClasses([
            desktopClasses.benefitsGrid,
            mobileClasses.benefitsGrid
          ])}
        >
          {benefitItems.map((benefit) => (
            <BenefitItem key={benefit.title} data={benefit} />
          ))}
        </div>
      </div>
    </>
  );
};

export default HeroSection;
